import { USER_ROLES, type User, type UserRole } from "@clinic/shared";
import { useMemo, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Badge,
  Button,
  EmailLink,
  EmptyState,
  Icon,
  MenuItem,
  ConfirmDialog,
  PageHeader,
  PersonName,
  PhoneLink,
  RowMenu,
  SearchField,
  Select,
  Switch,
  Table,
  TotalBadge,
  usePageParams,
  usePersonName,
  useToast,
  type Column,
} from "@clinic/ui";
import { useSession } from "@web/shared/providers/session";
import {
  useDeleteUser,
  useInviteUser,
  useSendPasswordReset,
  useUnlockUser,
  useUpdateUser,
} from "@web/modules/users/queries";
import { useUsers } from "@web/shared/queries/users";
import { ResetPasswordModal } from "@web/modules/users/components/reset-password-modal";
import { UserFormModal } from "@web/modules/users/components/user-form-modal";
import { errorToast } from "@web/shared/lib/api-error";
import { formatDate, formatTime } from "@web/shared/lib/format";
import { isRefetching } from "@clinic/ui/lib/use-delayed-loading";

export function UsersPage(): JSX.Element {
  const { t } = useTranslation();
  const displayName = usePersonName();
  const toast = useToast();
  const invite = useInviteUser();
  const sendReset = useSendPasswordReset();
  const removeUser = useDeleteUser();
  const unlock = useUnlockUser();
  const { user: currentUser, can } = useSession();

  const send = async (
    run: Promise<unknown>,
    successKey: "users.inviteSent" | "users.resetLinkSent" | "users.unlocked",
  ): Promise<void> => {
    try {
      await run;
      toast.success(successKey);
    } catch (error) {
      toast.error(...errorToast(error));
    }
  };

  const { page, perPage, setPage, setPerPage, resetPage } = usePageParams();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<UserRole | "">("");
  const [formUserId, setFormUserId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);

  const query = useUsers({
    page,
    limit: perPage,
    ...(search !== "" && { search }),
    ...(role !== "" && { role }),
  });
  const updateUser = useUpdateUser();

  const remove = async (): Promise<void> => {
    if (!deleting) {
      return;
    }

    try {
      await removeUser.mutateAsync(deleting.id);
      toast.success("users.deleted");
    } catch (error) {
      toast.error(...errorToast(error));
      throw error;
    }
  };

  const toggleActive = async (row: User): Promise<void> => {
    try {
      await updateUser.mutateAsync({ id: row.id, body: { isActive: !row.isActive } });
      toast.success("users.updated");
    } catch (error) {
      toast.error(...errorToast(error));
    }
  };

  const columns = useMemo<Column<User>[]>(
    () => [
      {
        key: "name",
        header: "users.name",
        primary: true,
        render: (row) => (
          <span className="flex items-center gap-3">
            <Avatar
              name={displayName(row.name)}
              tintKey={row.id}
              src={row.photoUrl}
              data-testid="user-avatar"
            />
            <span className="flex min-w-0 flex-col leading-label">
              <PersonName
                name={row.name}
                showBoth
                data-testid="user-name"
                className="truncate font-medium text-ink"
              />
              {row.email !== null && row.email !== undefined && (
                <EmailLink
                  value={row.email}
                  className="hidden break-all text-label md:inline-flex"
                />
              )}
            </span>
          </span>
        ),
      },
      {
        key: "phone",
        header: "users.phone",
        render: (row) => <PhoneLink value={row.phone} />,
      },
      {
        key: "email",
        header: "users.email",
        hideOnDesktop: true,
        render: (row) => <EmailLink value={row.email} />,
      },
      {
        key: "role",
        header: "users.role",
        render: (row) => (
          <Badge tone="info" data-testid="user-role">
            {t(`roles.${row.role}`)}
          </Badge>
        ),
      },
      {
        key: "status",
        header: "users.status",
        render: (row) => (
          <div className="flex items-center gap-2">
            <Switch
              data-testid="user-active-switch"
              checked={row.isActive}
              disabled={row.id === currentUser?.id}
              onCheckedChange={() => void toggleActive(row)}
              label={row.isActive ? t("users.deactivate") : t("users.activate")}
              hideLabel
            />
            <span data-testid="user-active-label" className="text-label text-ink-muted">
              {row.isActive ? t("users.active") : t("users.inactive")}
            </span>

            {!row.activated && (
              <Badge tone="warning" data-testid="user-pending">
                {t("users.pending")}
              </Badge>
            )}

            {row.loginLockedUntil !== null && (
              <Badge tone="danger" data-testid="user-locked">
                {t("users.locked", { time: formatTime(row.loginLockedUntil) })}
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: "createdAt",
        header: "audit.when",
        hideOnMobile: true,
        render: (row) => formatDate(row.createdAt),
      },
      {
        key: "actions",
        header: "common.actions",
        actions: true,
        besideTitleOnMobile: true,
        render: (row) => (
          <RowMenu label={t("users.rowMenu")} data-testid="user-menu">
            {can("users.update") && (
              <MenuItem
                icon="edit"
                data-testid="user-menu-edit"
                onSelect={() => {
                  setFormUserId(row.id);
                  setFormOpen(true);
                }}
              >
                {t("common.edit")}
              </MenuItem>
            )}

            {!row.activated && row.email && row.isActive && can("users.invite") && (
              <MenuItem
                icon="mail"
                data-testid="user-menu-resend-invite"
                onSelect={() => void send(invite.mutateAsync(row.id), "users.inviteSent")}
              >
                {t("users.resendInvite")}
              </MenuItem>
            )}

            {row.activated && row.email && row.isActive && can("users.sendPasswordReset") && (
              <MenuItem
                icon="key"
                data-testid="user-menu-send-reset"
                onSelect={() => void send(sendReset.mutateAsync(row.id), "users.resetLinkSent")}
              >
                {t("users.sendResetLink")}
              </MenuItem>
            )}

            {row.loginLockedUntil !== null && can("users.unlock") && (
              <MenuItem
                icon="lock"
                data-testid="user-menu-unlock"
                onSelect={() => void send(unlock.mutateAsync(row.id), "users.unlocked")}
              >
                {t("users.unlock")}
              </MenuItem>
            )}

            {!row.email && can("users.resetPassword") && (
              <MenuItem
                icon="key"
                data-testid="user-menu-reset-password"
                onSelect={() => setResetUser(row)}
              >
                {t("users.resetPassword")}
              </MenuItem>
            )}

            {row.id !== currentUser?.id && can("users.remove") && (
              <MenuItem
                icon="trash"
                tone="danger"
                data-testid="user-menu-delete"
                onSelect={() => setDeleting(row)}
              >
                {t("users.delete")}
              </MenuItem>
            )}
          </RowMenu>
        ),
      },
    ],
    [t, currentUser?.id, can, displayName],
  );

  const data = query.data;

  return (
    <div data-testid="users-page" className="flex flex-col gap-5">
      <PageHeader
        data-testid="users-header"
        title="users.title"
        subtitle="users.subtitle"
        primaryAction={
          <Button
            icon={<Icon name="user-plus" />}
            data-testid="users-create"
            onClick={() => {
              setFormUserId(null);
              setFormOpen(true);
            }}
          >
            {t("users.create")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchField
          data-testid="users-search"
          className="w-full min-w-0 sm:max-w-md sm:flex-1"
          label={t("common.search")}
          shortcut="/"
          placeholder={t("users.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            resetPage();
          }}
          clearLabel={t("common.clear")}
          onClear={() => {
            setSearch("");
            resetPage();
          }}
        />

        <Select
          data-testid="users-filter-role"
          className="w-full sm:w-48"
          aria-label={t("users.filterRole")}
          placeholder={t("common.all")}
          options={USER_ROLES.map((value) => ({ value, label: t(`roles.${value}`) }))}
          value={role}
          onChange={(event) => {
            setRole(event.target.value as UserRole | "");
            resetPage();
          }}
        />

        {query.data !== undefined && (
          <TotalBadge data-testid="users-count" className="ms-auto" total={query.data.total} />
        )}
      </div>

      <Table
        data-testid="users-table"
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={query.isPending}
        isRefreshing={isRefetching(query)}
        empty={
          <EmptyState
            data-testid="users-empty"
            title="users.empty"
            hint="users.emptyHint"
            action={
              <Button
                icon={<Icon name="user-plus" />}
                data-testid="users-empty-create"
                onClick={() => {
                  setFormUserId(null);
                  setFormOpen(true);
                }}
              >
                {t("users.create")}
              </Button>
            }
          />
        }
        {...(data && {
          pagination: {
            page: data.page,
            totalPages: data.totalPages,
            onPageChange: setPage,
            perPage,
            onPerPageChange: setPerPage,
          },
        })}
      />

      <UserFormModal
        data-testid="user-form-modal"
        open={formOpen}
        onOpenChange={setFormOpen}
        userId={formUserId}
      />
      <ResetPasswordModal
        data-testid="reset-password-modal"
        open={resetUser !== null}
        onOpenChange={(open) => {
          if (!open) {
            setResetUser(null);
          }
        }}
        user={resetUser}
      />

      <ConfirmDialog
        data-testid="user-delete-modal"
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="users.deleteQuestion"
        titleValues={{ name: displayName(deleting?.name) }}
        confirmLabel="users.delete"
        onConfirm={remove}
      />
    </div>
  );
}
